import { SIDEBAR_USAGE_REFETCH_MS, SIDEBAR_USAGE_STALE_MS, consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { UsageMeter, UsageMeterSkeleton } from "@sagot/ui";
import { useBillingPlans } from "../features/workspace/billing/use-billing";
import { useBillingOverview, useFormatters, type BillingOverviewRefresh } from "../features/workspace/shared";
import { usePermissions } from "../session/permissions";
import { useWorkspaceContext } from "../session/workspace-context";
import { hasHigherPlan, sidebarUsageOf } from "./usage-model";

const REFRESH: BillingOverviewRefresh = {
  staleTime: SIDEBAR_USAGE_STALE_MS,
  refetchInterval: SIDEBAR_USAGE_REFETCH_MS,
  refetchOnWindowFocus: true,
};

/**
 * Sidebar "Messages this month" card (mockup `sidebarFooter()`), above the user menu. Shown to members
 * who can open Billing (`billing.view`), because the card links there and the numbers come from its
 * overview; "Upgrade" needs `billing.manage` and a plan above the current one. A failed load hides the
 * card instead of breaking the sidebar.
 */
export function SidebarUsage() {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const fmt = useFormatters();
  const permissions = usePermissions();
  const canView = permissions.can("billing.view");
  const canManage = permissions.can("billing.manage") && !permissions.readOnly;
  const overview = useBillingOverview(canView, REFRESH);
  const plans = useBillingPlans(canManage && overview.isSuccess);

  if (!canView) return null;
  if (overview.isPending) return <UsageMeterSkeleton />;
  if (!overview.data) return null;

  const usage = sidebarUsageOf(overview.data);
  const unlimited = usage.limit === null;
  const canUpgrade = canManage && !unlimited && plans.data !== undefined && hasHigherPlan(plans.data, overview.data.plan.key);
  const lead = [
    unlimited ? t("shell.usageUsed", { value: fmt.number(usage.used) }) : null,
    usage.periodEnd ? t("shell.resets", { date: fmt.shortDate(usage.periodEnd) }) : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const status = { normal: undefined, warning: t("shell.usageLow", { value: fmt.number(usage.remaining ?? 0) }), danger: t("shell.usageLimitReached") }[usage.tone];

  return (
    <UsageMeter
      href={consoleRoutes.billing(summary.slug)}
      label={t("shell.messagesThisMonth")}
      used={usage.used}
      limit={usage.limit}
      valueText={usage.limit === null ? t("shell.usageUnlimited") : `${fmt.number(usage.used)} / ${fmt.number(usage.limit)}`}
      tone={usage.tone}
      status={status}
      footnote={
        <>
          {lead}
          {canUpgrade ? (
            <>
              {lead ? " · " : null}
              <span className="font-semibold text-brand-300">{t("shell.upgrade")}</span>
            </>
          ) : null}
        </>
      }
    />
  );
}
